import { useCallback } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { logger } from '@/logger';

export type ImageTagResult = {
	tags: string[];
	alt_text: string;
	tagging_status: string;
};

/**
 * Computes vision tags for one image WITHOUT writing the business profile, so
 * the caller can fold them into a save it already issues (no second writer
 * racing that save).
 *
 * Resolves `null` when the request never reached the server (offline, 401, 5xx,
 * aborted). That is distinct from the server answering
 * `{tagging_status: 'failed'}`, which means the vision call was attempted and
 * missed. Callers must keep the two apart: `"failed"` is terminal — nothing ever
 * resets it — so writing it for a request that never ran would permanently
 * exclude the photo from the server-side backfill that would otherwise retry it.
 */
export function useTagImage(userId: string | undefined) {
	const api = useBridgewayApi();

	return useCallback(
		async (url: string): Promise<ImageTagResult | null> => {
			if (!userId || !url) return null;
			try {
				const body = await api.post<ImageTagResult>(
					`/api/user/${userId}/business-profile/tag-image`,
					{ url },
				);
				return body ?? null;
			} catch (err) {
				logger.error('Failed to tag image', err);
				return null;
			}
		},
		[api, userId],
	);
}
